import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  TICKET_STATUSES,
  TICKET_TYPES,
  ticketDisplaySubject,
  type AgentSummary,
  type Category,
  type TicketStatus,
  type TicketSummary,
  type TicketType,
} from '@t3/shared';
import { ChevronDown, ChevronUp, Inbox, Search, X } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { StatusBadge, TypeBadge } from '../components/Card.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { ViewsSidebar, type ViewDefinition } from '../components/ViewsSidebar.tsx';
import { useHiddenStatuses } from '../hooks/useHiddenStatuses.ts';

// Keep in sync with backend SORT_COLUMNS in tickets repository.
type SortKey =
  | 'displayNumber'
  | 'type'
  | 'subject'
  | 'organizationName'
  | 'status'
  | 'assignedAgentName'
  | 'updatedAt';
type SortDir = 'asc' | 'desc';

const SORT_KEYS = new Set<SortKey>([
  'displayNumber',
  'type',
  'subject',
  'organizationName',
  'status',
  'assignedAgentName',
  'updatedAt',
]);

const VIEWS: ViewDefinition[] = [
  { id: 'my-open', label: 'My open tickets', search: 'assignedAgentId=me&closed=false' },
  { id: 'my-client-replied', label: 'My client replied', search: 'assignedAgentId=me&status=client_responded' },
  { id: 'all-open', label: 'All open tickets', search: 'closed=false' },
  { id: 'all-new', label: 'All new tickets', search: 'status=new' },
  { id: 'backlog', label: 'Backlog', search: 'status=backlog' },
  { id: 'all-solved', label: 'All solved tickets', search: 'status=solved' },
  { id: 'all-cancelled', label: 'Cancelled tickets', search: 'status=cancelled' },
  { id: 'all', label: 'All tickets', search: '' },
  // Spam view passes spam=only; everything else implicitly defaults to
  // spam=exclude server-side, so spam stays out of the working views.
  { id: 'spam', label: 'Spam', search: 'spam=only' },
];

export function TicketsListPage(): React.ReactElement {
  useDocumentTitle('Tickets');
  const { agent } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const hiddenStatuses = useHiddenStatuses();

  useEffect(() => {
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents))
      .catch(() => setAgents([]));
    apiFetch<{ categories: Category[] }>('/api/categories')
      .then((r) => setCategories(r.categories))
      .catch(() => setCategories([]));
  }, []);

  function toggleOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll(visible: TicketSummary[]) {
    if (visible.every((t) => selected.has(t.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((t) => t.id)));
    }
  }

  async function bulkStatus(status: string) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ updated: number }>('/api/tickets/bulk-status', {
        method: 'POST',
        json: { ids: [...selected], status },
      });
      toast.success(`Updated status on ${r.updated} ticket${r.updated === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk status failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkAssign(assignedAgentId: string | null) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ updated: number }>('/api/tickets/bulk-assign', {
        method: 'POST',
        json: { ids: [...selected], assignedAgentId },
      });
      toast.success(`Reassigned ${r.updated} ticket${r.updated === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk assign failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkSpam(isSpam: boolean) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ updated: number }>('/api/tickets/bulk-spam', {
        method: 'POST',
        json: { ids: [...selected], isSpam, reason: isSpam ? 'manual' : undefined },
      });
      toast.success(
        isSpam
          ? `Marked ${r.updated} ticket${r.updated === 1 ? '' : 's'} as spam.`
          : `Restored ${r.updated} ticket${r.updated === 1 ? '' : 's'} from spam.`,
      );
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk spam update failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkCategory(categoryId: string | null) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ updated: number }>('/api/tickets/bulk-category', {
        method: 'POST',
        json: { ids: [...selected], categoryId },
      });
      toast.success(`Re-categorized ${r.updated} ticket${r.updated === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk category change failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    // Two confirmations: a friendly count first, then an explicit yes/no.
    if (!window.confirm(
      `Permanently delete ${selected.size} ticket${selected.size === 1 ? '' : 's'}?\n\n` +
        'This removes every message, time entry, attachment, and CSAT survey on each one. ' +
        'This cannot be undone.',
    )) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ deleted: number; failures: Array<{ ticketId: string; error: string }> }>(
        '/api/tickets/bulk-delete',
        { method: 'POST', json: { ids: [...selected] } },
      );
      if (r.failures.length > 0) {
        toast.error(
          `Deleted ${r.deleted}; ${r.failures.length} failed (check audit log for details).`,
        );
      } else {
        toast.success(`Deleted ${r.deleted} ticket${r.deleted === 1 ? '' : 's'}.`);
      }
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function reload() {
    if (apiQuery === null) return;
    const res = await apiFetch<{ tickets: TicketSummary[] }>(`/api/tickets${apiQuery ? `?${apiQuery}` : ''}`);
    setTickets(res.tickets);
  }

  const type = (searchParams.get('type') as TicketType | null) ?? '';
  const status = (searchParams.get('status') as TicketStatus | null) ?? '';
  const closedParam = searchParams.get('closed');
  const assignedAgentParam = searchParams.get('assignedAgentId');
  const qParam = searchParams.get('q') ?? '';
  const subjectQParam = searchParams.get('subjectQ') ?? '';
  const orgQParam = searchParams.get('orgQ') ?? '';
  const updatedFromParam = searchParams.get('updatedFrom') ?? '';
  const updatedToParam = searchParams.get('updatedTo') ?? '';
  const spamParam = searchParams.get('spam') ?? '';
  const inSpamView = spamParam === 'only';
  const sortRaw = searchParams.get('sort');
  const sort: SortKey = SORT_KEYS.has(sortRaw as SortKey) ? (sortRaw as SortKey) : 'updatedAt';
  const dirRaw = searchParams.get('dir');
  const dir: SortDir = dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : 'desc';

  // Local input state separate from URL so typing doesn't refetch on every
  // keystroke — debounced into the URL below.
  const [searchInput, setSearchInput] = useState(qParam);
  useEffect(() => {
    setSearchInput(qParam);
  }, [qParam]);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === qParam) return;
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput, qParam, searchParams, setSearchParams]);

  // Debounced per-column text filters. Same pattern as the search box —
  // local state drives the input; a 250ms timeout commits it to the URL.
  const [subjectInput, setSubjectInput] = useState(subjectQParam);
  useEffect(() => {
    setSubjectInput(subjectQParam);
  }, [subjectQParam]);
  useEffect(() => {
    const trimmed = subjectInput.trim();
    if (trimmed === subjectQParam) return;
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set('subjectQ', trimmed);
      else next.delete('subjectQ');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [subjectInput, subjectQParam, searchParams, setSearchParams]);

  const [orgInput, setOrgInput] = useState(orgQParam);
  useEffect(() => {
    setOrgInput(orgQParam);
  }, [orgQParam]);
  useEffect(() => {
    const trimmed = orgInput.trim();
    if (trimmed === orgQParam) return;
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set('orgQ', trimmed);
      else next.delete('orgQ');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgInput, orgQParam, searchParams, setSearchParams]);

  const apiQuery = useMemo(() => {
    if (!agent) return null;
    const p = new URLSearchParams();
    if (type) p.set('type', type);
    if (status) p.set('status', status);
    if (closedParam) p.set('closed', closedParam);
    if (assignedAgentParam) {
      p.set('assignedAgentId', assignedAgentParam === 'me' ? agent.id : assignedAgentParam);
    }
    if (qParam.trim()) p.set('q', qParam.trim());
    if (subjectQParam.trim()) p.set('subjectQ', subjectQParam.trim());
    if (orgQParam.trim()) p.set('orgQ', orgQParam.trim());
    if (updatedFromParam) p.set('updatedFrom', updatedFromParam);
    if (updatedToParam) p.set('updatedTo', updatedToParam);
    if (spamParam) p.set('spam', spamParam);
    // Only send sort to the server when it differs from the default
    // (updated_at desc), so the existing per-view counts URL stays stable.
    if (sort !== 'updatedAt') p.set('sort', sort);
    if (dir !== 'desc') p.set('dir', dir);
    return p.toString();
  }, [
    agent,
    type,
    status,
    closedParam,
    assignedAgentParam,
    qParam,
    subjectQParam,
    orgQParam,
    updatedFromParam,
    updatedToParam,
    spamParam,
    sort,
    dir,
  ]);

  useEffect(() => {
    if (apiQuery === null) return;
    let cancelled = false;
    apiFetch<{ tickets: TicketSummary[] }>(`/api/tickets${apiQuery ? `?${apiQuery}` : ''}`)
      .then((res) => {
        if (!cancelled) {
          setTickets(res.tickets);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load tickets.');
      });
    return () => {
      cancelled = true;
    };
  }, [apiQuery]);

  // Fetch a count per view in parallel for the sidebar badges. Refetches when
  // tickets reload, so the badge stays accurate after bulk status changes.
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!agent) return;
    let cancelled = false;
    Promise.all(
      VIEWS.map(async (v) => {
        const params = new URLSearchParams(v.search);
        const agentId = params.get('assignedAgentId');
        if (agentId === 'me') params.set('assignedAgentId', agent.id);
        try {
          const r = await apiFetch<{ count: number }>(
            `/api/tickets/count${params.toString() ? `?${params}` : ''}`,
          );
          return [v.id, r.count] as const;
        } catch {
          return [v.id, 0] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setViewCounts(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [agent, tickets]);

  const viewsWithCounts = useMemo<ViewDefinition[]>(
    () => VIEWS.map((v) => ({ ...v, count: viewCounts[v.id] })),
    [viewCounts],
  );

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  function toggleSort(key: SortKey) {
    const next = new URLSearchParams(searchParams);
    if (sort === key) {
      // same column: flip direction.
      next.set('sort', key);
      next.set('dir', dir === 'asc' ? 'desc' : 'asc');
    } else {
      // new column: default to ascending for text columns, descending
      // for the timestamp / numeric column.
      next.set('sort', key);
      next.set('dir', key === 'updatedAt' || key === 'displayNumber' ? 'desc' : 'asc');
    }
    // Drop defaults so the URL stays tidy.
    if (next.get('sort') === 'updatedAt') next.delete('sort');
    if (next.get('dir') === 'desc') next.delete('dir');
    setSearchParams(next);
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <ViewsSidebar title="Ticket views" views={viewsWithCounts} basePath="/tickets" />

      <div className="min-w-0 flex-1 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[var(--color-header)]">Tickets</h1>
            <p className="mt-1 text-[var(--color-muted)]">
              Click a column header to sort. Use the row beneath to filter.
            </p>
          </div>
          <Link to="/tickets/new">
            <Button>New ticket</Button>
          </Link>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search this view — subject, organization, or T-number"
            className="h-10 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white pl-9 pr-9 text-sm text-[var(--color-body)] outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--color-muted)] hover:text-[var(--color-body)]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        {tickets === null ? (
          <p className="text-[var(--color-muted)]">Loading…</p>
        ) : (
          <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
            {selected.size > 0 && tickets.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-t3-blue)]/30 bg-[var(--color-t3-blue)]/5 px-4 py-2 text-sm">
                <span className="font-semibold text-[var(--color-header)]">
                  {selected.size} selected
                </span>
                <select
                  value=""
                  onChange={(e) => e.target.value && void bulkStatus(e.target.value)}
                  disabled={bulkBusy}
                  className="h-9 min-w-[200px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
                >
                  <option value="">Set status…</option>
                  {TICKET_STATUSES.filter((s) => !hiddenStatuses.has(s)).map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <select
                  value=""
                  onChange={(e) =>
                    void bulkAssign(e.target.value === '__none' ? null : e.target.value || null)
                  }
                  disabled={bulkBusy}
                  className="h-9 min-w-[200px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
                >
                  <option value="">Assign to…</option>
                  <option value="__none">— Unassign —</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <select
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    void bulkCategory(e.target.value === '__none' ? null : e.target.value);
                  }}
                  disabled={bulkBusy}
                  className="h-9 min-w-[180px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
                >
                  <option value="">Set category…</option>
                  <option value="__none">— None (uncategorized) —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void bulkSpam(!inSpamView)}
                  disabled={bulkBusy}
                  className={
                    'h-9 rounded-[var(--radius-button)] border px-3 text-xs font-semibold transition disabled:opacity-60 ' +
                    (inSpamView
                      ? 'border-[var(--color-t3-blue)] text-[var(--color-t3-blue)] hover:bg-[var(--color-t3-blue)]/10'
                      : 'border-[var(--color-danger)]/40 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10')
                  }
                  title={
                    inSpamView
                      ? 'Restore selected tickets from spam'
                      : 'Move selected tickets to spam'
                  }
                >
                  {inSpamView ? 'Not spam' : 'Mark spam'}
                </button>
                {agent?.role === 'admin' && (
                  <button
                    type="button"
                    onClick={() => void bulkDelete()}
                    disabled={bulkBusy}
                    className="h-9 rounded-[var(--radius-button)] bg-[var(--color-danger)] px-3 text-xs font-semibold text-white transition hover:brightness-95 disabled:opacity-60"
                    title="Permanently delete the selected tickets (admin only)"
                  >
                    Delete selected
                  </button>
                )}
                {bulkBusy && (
                  <span className="text-xs text-[var(--color-muted)]">Working…</span>
                )}
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  disabled={bulkBusy}
                  className="ml-auto text-xs text-[var(--color-muted)] hover:underline disabled:opacity-60"
                >
                  Clear
                </button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={tickets.length > 0 && tickets.every((t) => selected.has(t.id))}
                      onChange={() => toggleAll(tickets)}
                      className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                    />
                  </th>
                  <SortTh label="#"        sortKey="displayNumber"      current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Type"     sortKey="type"               current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Subject"  sortKey="subject"            current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Org"      sortKey="organizationName"   current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Status"   sortKey="status"             current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Assigned" sortKey="assignedAgentName"  current={sort} dir={dir} onClick={toggleSort} />
                  <SortTh label="Updated"  sortKey="updatedAt"          current={sort} dir={dir} onClick={toggleSort} />
                </tr>
                <tr className="border-t border-[var(--color-border)] bg-white normal-case">
                  <th className="px-3 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-2 py-2">
                    <HeaderSelect value={type} onChange={(v) => updateFilter('type', v)} ariaLabel="Filter by type">
                      <option value="">All</option>
                      {TICKET_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </HeaderSelect>
                  </th>
                  <th className="px-2 py-2">
                    <HeaderText
                      value={subjectInput}
                      onChange={setSubjectInput}
                      placeholder="Search subject…"
                      ariaLabel="Filter by subject"
                    />
                  </th>
                  <th className="px-2 py-2">
                    <HeaderText
                      value={orgInput}
                      onChange={setOrgInput}
                      placeholder="Search org…"
                      ariaLabel="Filter by organization"
                    />
                  </th>
                  <th className="px-2 py-2">
                    <HeaderSelect value={status} onChange={(v) => updateFilter('status', v)} ariaLabel="Filter by status">
                      <option value="">All</option>
                      {TICKET_STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                      ))}
                    </HeaderSelect>
                  </th>
                  <th className="px-2 py-2">
                    <HeaderSelect
                      value={assignedAgentParam ?? ''}
                      onChange={(v) => updateFilter('assignedAgentId', v)}
                      ariaLabel="Filter by assignee"
                    >
                      <option value="">All</option>
                      {agent && <option value="me">— me —</option>}
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </HeaderSelect>
                  </th>
                  <th className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1">
                      <input
                        type="date"
                        aria-label="Updated from"
                        title="Updated on/after"
                        value={updatedFromParam}
                        onChange={(e) => updateFilter('updatedFrom', e.target.value)}
                        className="h-7 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-1.5 text-xs normal-case text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                      />
                      <input
                        type="date"
                        aria-label="Updated to"
                        title="Updated on/before"
                        value={updatedToParam}
                        onChange={(e) => updateFilter('updatedTo', e.target.value)}
                        className="h-7 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-1.5 text-xs normal-case text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <Inbox className="h-8 w-8 text-[var(--color-muted)]" />
                        <div className="text-sm font-semibold text-[var(--color-header)]">
                          No tickets match this view
                        </div>
                        <div className="max-w-md text-sm text-[var(--color-muted)]">
                          Try clearing the filter row, picking a different view (e.g. "All
                          tickets"), or creating a new ticket.
                        </div>
                        <Link to="/tickets/new">
                          <Button>New ticket</Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                )}
                {tickets.map((t) => (
                  <tr
                    key={t.id}
                    className={
                      'border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]/40 ' +
                      (selected.has(t.id) ? 'bg-[var(--color-t3-blue)]/5' : '')
                    }
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={() => toggleOne(t.id)}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">T-{t.displayNumber}</td>
                    <td className="px-4 py-3">
                      <TypeBadge type={t.type} />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/tickets/${t.id}`} className="font-medium text-[var(--color-t3-blue)] hover:underline">
                        {ticketDisplaySubject(t.clientTicketNumber, t.subject)}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{t.organizationName}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={t.status}>{t.status.replace(/_/g, ' ')}</StatusBadge>
                    </td>
                    <td className="px-4 py-3">{t.assignedAgentName ?? '—'}</td>
                    <td className="px-4 py-3 text-[var(--color-muted)]">
                      {new Date(t.updatedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SortTh({
  label,
  sortKey,
  current,
  dir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
}): React.ReactElement {
  const active = current === sortKey;
  return (
    <th className="px-4 py-3">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={
          'inline-flex items-center gap-1 uppercase tracking-wide hover:text-[var(--color-body)] ' +
          (active ? 'text-[var(--color-body)]' : '')
        }
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ChevronDown className="h-3.5 w-3.5 opacity-20" />
        )}
      </button>
    </th>
  );
}

function HeaderSelect({
  value,
  onChange,
  ariaLabel,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full max-w-[180px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-xs normal-case text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
    >
      {children}
    </select>
  );
}

function HeaderText({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  ariaLabel: string;
}): React.ReactElement {
  return (
    <input
      type="search"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full min-w-0 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-xs normal-case text-[var(--color-body)] outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
    />
  );
}
